import { useEffect } from 'react';
import { atom, useSetAtom } from 'jotai';

/**
 * Mirrors Tailwind's `md` breakpoint, which the intro card gates its content on.
 * Kept in sync by hand — the theme isn't readable at runtime.
 */
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';

/**
 * The interim intro card that sits between a Get Matched trigger and the brief
 * flow. Desktop triggers open this; only its Continue button opens the brief.
 */
export const getMatchedIntroOpenAtom = atom(false);

/**
 * The multi-step brief itself. Reached through the intro card's Continue button
 * on desktop, and straight from the trigger below `md`.
 */
export const getMatchedModalOpenAtom = atom(false);

export const useOpenGetMatchedModal = () => {
    const setIntroOpen = useSetAtom(getMatchedIntroOpenAtom);
    const setBriefOpen = useSetAtom(getMatchedModalOpenAtom);
    return () => {
        if (typeof window === 'undefined') return;
        // The intro card is a desktop-width design and renders nothing below
        // `md`, so mobile skips it and enters the brief directly.
        if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
            setIntroOpen(true);
            return;
        }
        setBriefOpen(true);
    };
};

/**
 * Closes the intro card once the viewport drops below `md`. Triggers never open
 * it there, but a viewport can cross the breakpoint while it is up, which would
 * leave the overlay, scroll lock and focus trap in place with the card itself
 * hidden behind them. The brief has no such guard — it renders at every width.
 */
export const useCloseBelowDesktop = (
    open: boolean,
    setOpen: (open: boolean) => void,
) => {
    useEffect(() => {
        if (!open || typeof window === 'undefined') return;

        const query = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const closeIfNarrow = () => {
            if (!query.matches) setOpen(false);
        };

        closeIfNarrow();
        query.addEventListener('change', closeIfNarrow);
        return () => query.removeEventListener('change', closeIfNarrow);
    }, [open, setOpen]);
};
